/**
 * Scheduler domain contracts.
 *
 * Instants (`TimeSlot.start`) always arrive as UTC ISO-8601 and are rendered in
 * the therapist's timezone; never format them with the browser's zone.
 */

import type { NoticeChannel } from "../../../shared/domain/noticeChannel";

export enum OnlinePlatform {
    ZOOM = "zoom",
    GOOGLE_MEET = "google_meet",
    /** A Teams meeting in the therapist's Microsoft 365 work or school account. */
    MICROSOFT_TEAMS = "microsoft_teams",
    /** Alchely's own video call. Hosted by us, so never "not connected". */
    ALCHELY = "alchely",
}

export enum ScheduledStatus {
    SCHEDULED = "scheduled",
    COMPLETED = "completed",
    CANCELLED = "cancelled",
}

export enum BookedBy {
    THERAPIST = "therapist",
    PATIENT = "patient",
}

/** Matches the backend `SessionUbication` values, space included. */
export enum SessionUbication {
    ONLINE = "online",
    IN_PERSON = "in person",
}

export const MIN_SESSION_DURATION = 15;
export const MAX_SESSION_DURATION = 240;

/** Notice a session reminder gives, in hours: an hour at least, a week at most. */
export const MIN_REMINDER_HOURS = 1;
export const MAX_REMINDER_HOURS = 168;

/** The lead times offered in the picker. Any value in range is valid to the
 *  API; these are the ones worth one click. */
export const REMINDER_HOUR_CHOICES = [1, 2, 4, 24, 48, 72, 168] as const;

/** Notice periods offered for patient cancellation, in hours. 0 = until it starts. */
export const PATIENT_CANCELLATION_HOUR_CHOICES = [0, 2, 4, 12, 24, 48, 72] as const;
/** Mirrors the backend: one WhatsApp message, not a letter. */
export const MAX_WHATSAPP_REMINDER_TEMPLATE_LENGTH = 1000;

/** 0 = Monday … 6 = Sunday, matching the backend. */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const DAYS_OF_WEEK: DayOfWeek[] = [0, 1, 2, 3, 4, 5, 6];

export interface SchedulerSettings {
    timezone: string;
    /** Minutes an individual session is booked for. */
    default_duration: number;
    /** Minutes a couple's session is booked for; the individual length until set. */
    couple_duration: number;
    /** Minutes a group's session is booked for; the individual length until set. */
    group_duration: number;
    zoom_connected: boolean;
    google_connected: boolean;
    /** A Microsoft 365 work or school account: Teams meetings and Outlook events. */
    microsoft_connected: boolean;
    /** Whether that grant also reads Teams transcripts (needs admin consent). */
    microsoft_transcripts_enabled: boolean;
    /** Whether patients may book their own sessions from the portal. */
    patient_booking_enabled: boolean;
    /** Whether a reminder goes out before each booked session. Off by default. */
    reminder_enabled: boolean;
    /** How much notice the reminder gives, in hours. */
    reminder_hours_before: number;
    reminder_to_patient: boolean;
    reminder_to_therapist: boolean;
    /** The channel of the patient's reminder: a permission to try WhatsApp,
     *  never a bypass of the patient's own consent. */
    reminder_notice_channel: NoticeChannel;
    /** Whether a new booking is announced, and to whom. On for both by default. */
    booking_notice_enabled: boolean;
    booking_notice_to_patient: boolean;
    booking_notice_to_therapist: boolean;
    /** The channel of the patient's copy, on the same terms as the reminder's. */
    booking_notice_channel: NoticeChannel;
    /** Whether a reschedule or cancellation is announced, and to whom. */
    change_notice_enabled: boolean;
    change_notice_to_patient: boolean;
    change_notice_to_therapist: boolean;
    /** The channel of the patient's copy, on the same terms as the reminder's. */
    change_notice_channel: NoticeChannel;
    /** Whether patients may cancel their own appointments. On by default. */
    patient_cancellation_enabled: boolean;
    /** Notice a patient must give, in hours. 0 means until it starts. */
    patient_cancellation_min_hours: number;
    /**
     * The reminder the agenda's WhatsApp button writes, with `{name}`,
     * `{date}`, `{time}` and `{link}` to fill in. Null means the default one.
     */
    whatsapp_reminder_template?: string | null;
    google_calendar_id?: string | null;
}

export interface SchedulerSettingsUpdate {
    timezone?: string;
    default_duration?: number;
    couple_duration?: number;
    group_duration?: number;
    patient_booking_enabled?: boolean;
    reminder_enabled?: boolean;
    reminder_hours_before?: number;
    reminder_to_patient?: boolean;
    reminder_to_therapist?: boolean;
    reminder_notice_channel?: NoticeChannel;
    booking_notice_enabled?: boolean;
    booking_notice_to_patient?: boolean;
    booking_notice_to_therapist?: boolean;
    booking_notice_channel?: NoticeChannel;
    change_notice_enabled?: boolean;
    change_notice_to_patient?: boolean;
    change_notice_to_therapist?: boolean;
    change_notice_channel?: NoticeChannel;
    patient_cancellation_enabled?: boolean;
    patient_cancellation_min_hours?: number;
    /** Blank goes back to the default message. */
    whatsapp_reminder_template?: string;
}

export interface AvailabilityWindow {
    id: string;
    day_of_week: number;
    /** `HH:MM:SS` wall-clock time in the therapist's timezone. */
    start_time: string;
    end_time: string;
    is_active: boolean;
}

export type AvailabilityWindowInput = Omit<AvailabilityWindow, "id">;

export interface TimeSlot {
    /** UTC ISO-8601 instant. */
    start: string;
    duration_minutes: number;
}

export interface SlotList {
    date: string;
    timezone: string;
    slots: TimeSlot[];
}

export interface TherapistSpace {
    id: string;
    name: string;
    address?: string | null;
    notes?: string | null;
    is_public?: boolean;
}

export interface TherapistSpaceInput {
    name: string;
    address?: string | null;
    notes?: string | null;
    /** Whether this address appears on the therapist's public profile. Off
     *  unless asked for: one of a therapist's spaces is frequently their home. */
    is_public?: boolean;
}

export interface ScheduledSession {
    id: string;
    /**
     * Exactly one of `patient_id` / `couple_patient_id` / `group_patient_id`
     * is set. Anything deriving a label must branch on which, not assume
     * `patient_id`.
     */
    patient_id: string | null;
    couple_patient_id?: string | null;
    group_patient_id?: string | null;
    /** The subject's display name, resolved server-side. */
    subject_label?: string | null;
    title: string;
    /** Naive local date-time in the therapist's timezone. */
    date: string;
    hour: string;
    duration: number;
    ubication: string;
    scheduled_status: ScheduledStatus;
    booked_by: BookedBy;
    meeting_link?: string | null;
    online_platform?: string | null;
    space_id?: string | null;
    /** The session this booking is held as, opened pending when it was booked. */
    session_id?: string | null;
}

export interface ScheduleSessionInput {
    /** Exactly one of these. The backend refuses more than one, or none. */
    patient_id?: string | null;
    couple_patient_id?: string | null;
    group_patient_id?: string | null;
    /** UTC ISO-8601 instant. */
    start: string;
    ubication: SessionUbication;
    online_platform?: OnlinePlatform | null;
    space_id?: string | null;
    title?: string | null;
    therapy_type?: string;
    /** Open the session now, pending, so it can be prepared before the day. */
    open_session?: boolean;
    /**
     * A training centre's mentoring: the module of the centre's courses it
     * works on. Its session takes it, frozen as the module then reads.
     */
    course_module_id?: string | null;
}

/**
 * URL-facing provider names; `google` covers Calendar and Meet in one grant,
 * `microsoft` Outlook and Teams in another.
 */
export type IntegrationProvider = "zoom" | "google" | "microsoft";

export interface OAuthOptions {
    /** Microsoft only: also ask to read Teams transcripts. */
    transcripts?: boolean;
}

export interface AuthorizeUrl {
    authorize_url: string;
    /** Stored before redirecting and compared on the way back. */
    state: string;
}

export interface PatientBookingAvailability {
    booking_enabled: boolean;
    timezone?: string | null;
    default_duration?: number | null;
}

/** Availability windows on the same weekday must not overlap. */
export function hasOverlap(windows: AvailabilityWindowInput[]): boolean {
    const active = windows.filter((window) => window.is_active);
    for (let i = 0; i < active.length; i += 1) {
        for (let j = i + 1; j < active.length; j += 1) {
            const a = active[i];
            const b = active[j];
            if (a.day_of_week !== b.day_of_week) continue;
            if (a.start_time < b.end_time && b.start_time < a.end_time) return true;
        }
    }
    return false;
}

/** A window is only valid when it ends strictly after it starts. */
export function isValidWindow(window: AvailabilityWindowInput): boolean {
    return window.end_time > window.start_time;
}

/** Who a session is booked with: one patient, one couple or one group. */
export type SubjectKind = "patient" | "couple" | "group";

/** The kind of session a subject is booked into; each can last its own time. */
export type BookingSessionType = "individual" | "couple" | "group";

/**
 * A booking subject the caller already knows — the patient or couple whose
 * page the therapist is on. `label` is what the UI shows so the therapist can
 * confirm the subject before committing a time.
 */
export interface ScheduleSubject {
    kind: SubjectKind;
    id: string;
    label: string;
}

/**
 * How to narrow a session list.
 *
 * `participant` means every session this person attends, including as part of
 * a couple — what a patient page asks. `patient` is the stricter "theirs
 * alone", which a filter wants.
 */
export type SubjectFilterKind = SubjectKind | "participant";

export interface UpcomingSubjectFilter {
    kind: SubjectFilterKind;
    id: string;
}

/** Why a patient may, or may not, cancel an appointment themselves. */
export type CancellationStatus =
    | "allowed"
    | "disabled"
    | "shared_session"
    | "already_cancelled"
    | "started"
    | "too_late";

/**
 * What a cancel link shows its holder. Logistics only: never the title, the
 * patient's name or the meeting link — the link may have been forwarded.
 */
export interface PublicCancellation {
    status: CancellationStatus;
    /** True once the appointment is cancelled by this or an earlier request. */
    cancelled: boolean;
    /** Naive local date-time in `timezone`. */
    date: string;
    hour: string;
    duration: number;
    timezone: string;
    therapist_name: string;
    /** Aware ISO instant: the last moment cancelling is allowed. */
    deadline: string;
}

/**
 * The exact shape of a cancel link token: a fixed-size payload, a dot and a
 * fixed-size signature, all base64url. Anything else is not a link we issued,
 * and is never sent to the API — a free-form value there could steer the
 * request somewhere else.
 */
export const CANCEL_LINK_TOKEN_PATTERN = /^[A-Za-z0-9_-]{27}\.[A-Za-z0-9_-]{32}$/;

export const isCancelLinkToken = (value: string): boolean =>
    CANCEL_LINK_TOKEN_PATTERN.test(value);
